/**
 * Campaign Orders Management Component
 * Admin view for managing campaign quote requests with PO attachment
 */
import { useState, useEffect } from "react";
import {
  Package,
  FileText,
  Upload,
  Check,
  Clock,
  Truck,
  DollarSign,
  Building2,
  AlertTriangle,
  Tag,
  Loader2,
  ChevronDown,
  ChevronUp,
  Paperclip,
  CheckCircle,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Status configuration
const statusConfig = {
  pending: { label: "Pending", color: "bg-amber-100 text-amber-700", icon: Clock },
  quoted: { label: "Quoted", color: "bg-blue-100 text-blue-700", icon: FileText },
  accepted: { label: "Accepted", color: "bg-purple-100 text-purple-700", icon: Check },
  ordered: { label: "Order Placed", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  shipped: { label: "Shipped", color: "bg-cyan-100 text-cyan-700", icon: Truck },
  fulfilled: { label: "Fulfilled", color: "bg-green-100 text-green-700", icon: CheckCircle },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700", icon: X }
};

export default function CampaignOrdersManagement({ token }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  
  // PO Modal State
  const [showPOModal, setShowPOModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [poForm, setPOForm] = useState({
    po_number: "",
    po_date: "",
    po_notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, [token]);

  const fetchOrders = async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API}/api/campaign-quote-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setOrders(data.quote_requests || []);
      }
    } catch (err) {
      console.error("Failed to fetch campaign orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAttachPO = async () => {
    if (!selectedOrder || !poForm.po_number) {
      toast.error("Please enter a PO number");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/campaign-quote-requests/${selectedOrder.id}/attach-po`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(poForm)
      });

      if (res.ok) {
        toast.success("Purchase order attached");
        setShowPOModal(false);
        setSelectedOrder(null);
        setPOForm({ po_number: "", po_date: "", po_notes: "" });
        fetchOrders();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to attach PO");
      }
    } catch (err) {
      toast.error("Failed to attach PO");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkOrdered = async (order) => {
    try {
      const res = await fetch(`${API}/api/campaign-quote-requests/${order.id}/mark-ordered`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success("Order marked as placed");
        fetchOrders();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update status");
      }
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleUpdateStatus = async (order, newStatus) => {
    try {
      const res = await fetch(`${API}/api/campaign-quote-requests/${order.id}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success(`Status updated to ${newStatus}`);
        fetchOrders();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update status");
      }
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const openPOModal = (order) => {
    setSelectedOrder(order);
    setPOForm({
      po_number: order.purchase_order?.po_number || "",
      po_date: order.purchase_order?.po_date?.split("T")[0] || new Date().toISOString().split("T")[0],
      po_notes: order.purchase_order?.po_notes || ""
    });
    setShowPOModal(true);
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getUrgencyBadge = (urgency) => {
    const colors = {
      low: "bg-green-100 text-green-700",
      normal: "bg-amber-100 text-amber-700",
      high: "bg-orange-100 text-orange-700",
      critical: "bg-red-100 text-red-700"
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[urgency] || colors.normal}`}>
        {urgency?.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="campaign-orders-panel">
        <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-slate-800">Campaign Quote Requests</h3>
            <span className="px-2 py-0.5 bg-blue-500 text-white rounded-full text-xs font-bold">
              {orders.length}
            </span>
          </div>
        </div>

        {orders.length === 0 ? (
          <div className="p-6 text-center text-slate-500">
            <Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No campaign quote requests yet</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {orders.map(order => (
              <div key={order.id} className="p-4">
                {/* Order Header */}
                <div 
                  className="flex items-start justify-between cursor-pointer"
                  onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {getStatusBadge(order.status)}
                      {getUrgencyBadge(order.urgency)}
                      {order.purchase_order && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                          <Paperclip className="w-3 h-3" />
                          PO: {order.purchase_order.po_number}
                        </span>
                      )}
                    </div>
                    
                    <h4 className="font-medium text-slate-900">{order.product_name}</h4>
                    <p className="text-sm text-slate-600 mt-1">
                      {order.manufacturer} • MFR#: {order.manufacturer_number || "N/A"}
                    </p>
                    
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="text-emerald-600 font-medium">
                        {order.quantity_requested} units
                      </span>
                      <span className="text-slate-700">
                        ${order.estimated_total?.toFixed(2)}
                      </span>
                      <span className="text-slate-500 flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {order.hospital_name}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {expandedOrder === order.id ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedOrder === order.id && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Supplier</p>
                        <p className="text-sm font-medium">{order.supplier_name}</p>
                        <p className="text-xs text-slate-500">{order.supplier_email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Requester</p>
                        <p className="text-sm font-medium">{order.requester_name}</p>
                        <p className="text-xs text-slate-500">{order.requester_email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Created</p>
                        <p className="text-sm">{new Date(order.created_at).toLocaleString()}</p>
                      </div>
                      {order.order_placed_at && (
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Order Placed</p>
                          <p className="text-sm">{new Date(order.order_placed_at).toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                    
                    {order.notes && (
                      <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500 mb-1">Notes</p>
                        <p className="text-sm text-slate-700">{order.notes}</p>
                      </div>
                    )}

                    {/* Purchase Order Info (Internal) */}
                    {order.purchase_order && (
                      <div className="mb-4 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                        <p className="text-xs text-amber-600 mb-1 flex items-center gap-1">
                          <Paperclip className="w-3 h-3" />
                          Internal Purchase Order (Not shown to supplier)
                        </p>
                        <p className="text-sm font-medium text-slate-900">PO#: {order.purchase_order.po_number}</p>
                        {order.purchase_order.po_date && (
                          <p className="text-xs text-slate-600">Date: {new Date(order.purchase_order.po_date).toLocaleDateString()}</p>
                        )}
                        {order.purchase_order.po_notes && (
                          <p className="text-xs text-slate-600 mt-1">{order.purchase_order.po_notes}</p>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPOModal(order)}
                        className="text-amber-600 border-amber-200 hover:bg-amber-50"
                      >
                        <Paperclip className="w-4 h-4 mr-1" />
                        {order.purchase_order ? "Edit PO" : "Attach PO"}
                      </Button>
                      
                      {order.status !== "ordered" && order.status !== "fulfilled" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleMarkOrdered(order)}
                          className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Mark Order Placed
                        </Button>
                      )}
                      
                      {order.status === "ordered" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUpdateStatus(order, "fulfilled")}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Mark Fulfilled
                        </Button>
                      )}

                      {/* Status Dropdown */}
                      <select
                        value={order.status}
                        onChange={(e) => handleUpdateStatus(order, e.target.value)}
                        className="px-2 py-1 text-sm border border-slate-200 rounded-md"
                      >
                        <option value="pending">Pending</option>
                        <option value="quoted">Quoted</option>
                        <option value="accepted">Accepted</option>
                        <option value="ordered">Order Placed</option>
                        <option value="shipped">Shipped</option>
                        <option value="fulfilled">Fulfilled</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Attach PO Modal */}
      <Dialog open={showPOModal} onOpenChange={setShowPOModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="w-5 h-5 text-amber-600" />
              Attach Purchase Order
            </DialogTitle>
            <DialogDescription>
              This is an internal record and will not be shown to the supplier.
            </DialogDescription>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-4">
              {/* Order Summary */}
              <div className="bg-slate-50 rounded-lg p-3">
                <h4 className="font-medium text-sm text-slate-900">{selectedOrder.product_name}</h4>
                <p className="text-xs text-slate-600">
                  {selectedOrder.quantity_requested} units • ${selectedOrder.estimated_total?.toFixed(2)}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Supplier: {selectedOrder.supplier_name} | Hospital: {selectedOrder.hospital_name}
                </p>
              </div>

              {/* PO Form */}
              <div className="space-y-3">
                <div>
                  <Label htmlFor="po_number">PO Number *</Label>
                  <Input
                    id="po_number"
                    value={poForm.po_number}
                    onChange={(e) => setPOForm(prev => ({ ...prev, po_number: e.target.value }))}
                    placeholder="e.g., PO-2024-001234"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="po_date">PO Date</Label>
                  <Input
                    id="po_date"
                    type="date"
                    value={poForm.po_date}
                    onChange={(e) => setPOForm(prev => ({ ...prev, po_date: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="po_notes">Internal Notes (Optional)</Label>
                  <Textarea
                    id="po_notes"
                    value={poForm.po_notes}
                    onChange={(e) => setPOForm(prev => ({ ...prev, po_notes: e.target.value }))}
                    placeholder="Any internal notes about this purchase order..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPOModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAttachPO} 
              disabled={submitting || !poForm.po_number}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Paperclip className="w-4 h-4 mr-2" />
                  {selectedOrder?.purchase_order ? "Update PO" : "Attach PO"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
